<template>
  <div class="db-root" :style="{ direction: ['he', 'ar'].includes(adminLang) ? 'rtl' : 'ltr' }">

    <!-- ═══ LANG BAR (always visible) ══════════════════════════════ -->
    <div class="db-lang-bar-top">
      <button :class="['db-lang-btn', adminLang==='fr' ? 'db-lang-active' : '']" @click="setAdminLang('fr')">🇫🇷</button>
      <button :class="['db-lang-btn', adminLang==='en' ? 'db-lang-active' : '']" @click="setAdminLang('en')">🇬🇧</button>
      <button :class="['db-lang-btn', adminLang==='he' ? 'db-lang-active' : '']" @click="setAdminLang('he')">🇮🇱</button>
    </div>

    <!-- ═══ AUTH SCREEN ════════════════════════════════════════════ -->
    <div v-if="!currentUser" class="center-screen">
      <div class="db-card">
        <div class="db-logo">🗺️</div>
        <h2>{{ t('createNew') }}</h2>
        <p class="sub">{{ t('dbLoginDesc') }}</p>
        <div v-if="authError" class="msg error">{{ t('dbAuthError') }}</div>
        <button class="btn-google" :disabled="authLoading" @click="signIn">
          <span v-if="authLoading">{{ t('connecting') }}</span>
          <span v-else>
            <svg width="18" height="18" viewBox="0 0 48 48" style="vertical-align:middle;margin-right:8px"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.35-8.16 2.35-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/><path fill="none" d="M0 0h48v48H0z"/></svg>
            {{ t('signInGoogle') }}
          </span>
        </button>
      </div>
    </div>

    <!-- ═══ EMAIL NOT VERIFIED ══════════════════════════════════════ -->
    <div v-else-if="!currentUser.emailVerified" class="center-screen">
      <div class="db-card">
        <div class="db-logo">📧</div>
        <h2>{{ t('dbVerifyEmail') }}</h2>
        <p class="sub">{{ t('dbVerifyEmailMsg') }} <strong>{{ currentUser.email }}</strong></p>
        <div v-if="verifSent" class="msg success">{{ t('dbEmailSent') }}</div>
        <button class="btn-secondary mt-3" @click="resendVerification">{{ t('dbResendEmail') }}</button>
        <button class="btn-ghost mt-2" @click="recheckVerification">{{ t('dbCheckedEmail') }}</button>
        <button class="btn-link mt-3" @click="signOut">{{ t('dbSignOut') }}</button>
      </div>
    </div>

    <!-- ═══ DASHBOARD ════════════════════════════════════════════════ -->
    <div v-else>

      <!-- Topbar -->
      <div class="db-topbar">
        <div class="db-topbar-left">
          <span class="db-logo-sm">🗺️</span>
          <span class="db-title">{{ t('dbTitle') }}</span>
        </div>
        <div class="db-topbar-right">
          <span class="db-email">{{ currentUser.email }}</span>
          <button class="btn-logout" @click="signOut">{{ t('logout') }}</button>
        </div>
      </div>

      <div class="db-content">

        <!-- Create new game -->
        <div class="db-section">
          <div class="db-section-title">{{ t('dbCreateSection') }}</div>
          <div v-if="!showCreateForm">
            <div v-if="userPlan && userPlanConfig && myGames.length >= userPlanConfig.maxGames" class="plan-limit-msg">
              🔒 {{ t('planLimitReached') }}
              <a href="/pricing" class="plan-upgrade-link">{{ t('planUpgrade') }}</a>
            </div>
            <button v-else class="btn-create" @click="showCreateForm = true">{{ t('dbCreateBtn') }}</button>
          </div>
          <div v-else class="create-form">

            <div class="create-notice">{{ t('dbCreateNotice') }}</div>

            <!-- Nom d'affichage (en premier) -->
            <div class="field-group">
              <div class="field-label-row">
                <label class="field-label">{{ t('gameName') }}</label>
                <span class="info-icon" @click="toggleInfo('displayName')">ℹ️</span>
              </div>
              <div v-if="activeInfo === 'displayName'" class="info-box">{{ t('dbInfoDisplayName') }}</div>
              <input v-model="newDisplayName" class="form-input" :placeholder="adminLang === 'he' ? 'לדוגמה: מסלול גנבה — 2025' : adminLang === 'en' ? 'ex: Geneva - Servette - 47' : 'ex: Genève - Servette - 47'" @input="onDisplayNameInput" />
            </div>

            <!-- URL (auto-générée, modifiable) -->
            <div class="field-group">
              <div class="field-label-row">
                <label class="field-label">{{ t('urlIdentifier') }} <span style="font-size:11px;opacity:0.6">{{ t('urlNote') }}</span></label>
                <span class="info-icon" @click="toggleInfo('name')">ℹ️</span>
              </div>
              <div v-if="activeInfo === 'name'" class="info-box">{{ t('dbInfoUrl') }}</div>
              <input v-model="newGameName" class="form-input" :placeholder="adminLang === 'he' ? 'לדוגמה: GeneveServette47' : 'ex: GeneveServette47'"
                @input="urlManuallyEdited = true; nameCheckStatus = ''" />
              <div v-if="nameCheckStatus === 'taken'" class="msg error mt-1">❌ Ce nom est déjà pris.</div>
              <div v-if="nameCheckStatus === 'available'" class="msg success mt-1">✅ Disponible !</div>
            </div>

            <!-- Langues -->
            <div class="field-group">
              <div class="field-label-row">
                <label class="field-label">{{ t('availableLanguages') }}</label>
                <span class="info-icon" @click="toggleInfo('langs')">ℹ️</span>
              </div>
              <div v-if="activeInfo === 'langs'" class="info-box">{{ t('dbInfoLangs') }}</div>
              <div class="lang-checks">
                <label class="check-label"><input type="checkbox" value="he" v-model="newGameCfg.languages" /> {{ t('langHebrew') }}</label>
                <label class="check-label"><input type="checkbox" value="ar" v-model="newGameCfg.languages" /> {{ t('langArabic') }}</label>
                <label class="check-label"><input type="checkbox" value="fr" v-model="newGameCfg.languages" /> {{ t('langFrench') }}</label>
                <label class="check-label"><input type="checkbox" value="en" v-model="newGameCfg.languages" /> {{ t('langEnglish') }}</label>
              </div>
            </div>

            <!-- Carte -->
            <div class="field-group">
              <div class="field-label-row">
                <label class="field-label">{{ t('mapCenter') }}</label>
                <span class="info-icon" @click="toggleInfo('map')">ℹ️</span>
              </div>
              <div v-if="activeInfo === 'map'" class="info-box">{{ t('dbInfoMap') }}</div>
              <div class="coords-row">
                <button class="btn-map-pick btn-map-centered" type="button" @click="openMapPicker">📍 Carte</button>
                <button class="btn-coords-toggle" :class="{ active: showCreateCoords }" :title="showCreateCoords ? 'Masquer lat/lng' : 'Voir lat/lng'" @click="showCreateCoords = !showCreateCoords">👁</button>
              </div>
              <div v-if="showCreateCoords" class="coords-row mt-2">
                <input v-model="newGameCfg.mapLat" class="form-input" placeholder="Latitude  ex: 32.657" />
                <input v-model="newGameCfg.mapLng" class="form-input" placeholder="Longitude  ex: 35.417" />
              </div>
            </div>

            <!-- Dernier point + ordre de visite : cachés pour les abonnés (auto-calculés) -->
            <template v-if="!userPlan">
              <div class="field-group">
                <div class="field-label-row">
                  <label class="field-label">{{ t('lastPointNumber') }}</label>
                  <span class="info-icon" @click="toggleInfo('lastPoint')">ℹ️</span>
                </div>
                <div v-if="activeInfo === 'lastPoint'" class="info-box">{{ t('dbInfoLastPoint') }}</div>
                <input v-model.number="newGameCfg.lastPoint" type="number" class="form-input" placeholder="ex: 5" min="1" />
              </div>
              <div class="field-group">
                <div class="field-label-row">
                  <label class="field-label">{{ t('visitOrder') }}</label>
                  <span class="info-icon" @click="toggleInfo('order')">ℹ️</span>
                </div>
                <div v-if="activeInfo === 'order'" class="info-box">{{ t('dbInfoVisitOrder') }}</div>
                <input v-model="newGameCfg.visitOrder" class="form-input" placeholder="ex: 1, 2, 3, 4, 5" />
              </div>
            </template>

            <div class="row-buttons mt-3">
              <button class="btn-secondary" @click="showCreateForm = false; newGameName = ''; nameCheckStatus = ''">{{ t('cancel') }}</button>
              <button class="btn-success" @click="createGame" :disabled="!canCreate || creating">
                <span v-if="creating">{{ t('dbCreating') }}</span>
                <span v-else>{{ t('dbCreateAndAdd') }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- My games -->
        <div class="db-section">
          <div class="db-section-title">{{ t('dbGamesSection') }}</div>
          <div v-if="loadingGames" class="spinner-wrap"><div class="spinner"></div></div>
          <div v-else-if="myGames.length === 0" class="empty-state">{{ t('dbNoGames') }}</div>
          <div v-else class="games-grid">
            <div v-for="name in myGames" :key="name" class="game-card">
              <div class="game-card-name">{{ gameDisplayNames[name] || name }}</div>
              <div v-if="gameDisplayNames[name]" class="game-card-slug">🔗 {{ name }}</div>
              <div class="game-card-url">
                <span class="url-label">{{ t('dbUrlPlayers') }}</span>
                <a :href="gameUrl(name)" target="_blank" class="url-link">{{ gameUrl(name) }}</a>
                <button class="btn-copy" @click="copyUrl(name)" title="Copier">📋</button>
              </div>
              <div class="game-card-actions">
                <button class="btn-edit-game" @click="editGame(name)">{{ t('dbEditGame') }}</button>
                <button class="btn-clone-game" @click="askClone(name)">{{ t('dbCloneGame') }}</button>
                <button class="btn-delete-game" @click="askDelete(name)">{{ t('dbDeleteGame') }}</button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <div v-if="toast.show" :class="['toast-msg', toast.type]">{{ toast.text }}</div>

    <!-- DELETE CONFIRMATION MODAL -->
    <div v-if="deleteConfirm.show" class="modal-overlay" @click.self="deleteConfirm.show = false">
      <div class="modal-card">
        <div class="modal-icon">⚠️</div>
        <div class="modal-title">{{ t('dbDeleteTitle') }}</div>
        <div class="modal-body">
          <strong>« {{ deleteConfirm.name }} »</strong><br/><br/>
          {{ t('deleteMsg') }}
        </div>
        <div class="modal-input-row">
          <span class="modal-input-label">{{ t('dbDeleteConfirmLabel') }}</span>
          <input v-model="deleteConfirm.typed" class="form-input mt-1" :placeholder="deleteConfirm.name" />
        </div>
        <div class="modal-actions">
          <button class="btn-secondary" @click="deleteConfirm.show = false">{{ t('cancel') }}</button>
          <button
            class="btn-danger"
            :disabled="deleteConfirm.typed !== deleteConfirm.name || deleteConfirm.deleting"
            @click="confirmDelete"
          >
            <span v-if="deleteConfirm.deleting">{{ t('dbDeleting') }}</span>
            <span v-else>{{ t('dbDeletePermanently') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ═══ CLONE MODAL ══════════════════════════════════════════════ -->
    <div v-if="cloneModal.show" class="modal-overlay" @click.self="cloneModal.show = false">
      <div class="modal-card" style="max-width:480px">
        <div class="modal-icon">🔀</div>
        <div class="modal-title" style="color:#1a3a8f">{{ t('dbCloneTitle') }}</div>
        <div class="modal-body">
          <strong>« {{ cloneModal.sourceName }} »</strong>
        </div>

        <div class="field-group">
          <label class="field-label">{{ t('dbNewCourseName') }}</label>
          <input v-model="cloneModal.displayName" class="form-input" placeholder="ex: Parcours Ein Dor — Version 2" @input="onCloneDisplayNameInput" />
        </div>

        <div class="field-group">
          <label class="field-label">{{ t('urlIdentifier') }}</label>
          <input v-model="cloneModal.newName" class="form-input" placeholder="ex: ParcourseEinDor2"
            @input="cloneModal.urlManuallyEdited = true; cloneModal.nameStatus = ''" />
          <div v-if="cloneModal.nameStatus === 'taken'" class="msg error mt-1">{{ t('pointExists') }}</div>
          <div v-if="cloneModal.nameStatus === 'available'" class="msg success mt-1">✅</div>
        </div>

        <div class="field-group">
          <label class="field-label">{{ t('mapCenter') }}</label>
          <div class="coords-row">
            <input v-model="cloneModal.mapLat" class="form-input" placeholder="Latitude  ex: 32.657" />
            <input v-model="cloneModal.mapLng" class="form-input" placeholder="Longitude  ex: 35.417" />
            <button class="btn-map-pick" type="button" @click="openMapPicker('clone')">📍</button>
          </div>
        </div>

        <div class="modal-actions" style="justify-content:space-between">
          <button class="btn-secondary" @click="cloneModal.show = false">{{ t('cancel') }}</button>
          <button class="btn-success" :disabled="!canClone || cloneModal.cloning" @click="cloneGame">
            <span v-if="cloneModal.cloning">{{ t('dbCloning') }}</span>
            <span v-else>{{ t('dbCloneBtn') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ═══ MAP PICKER ══════════════════════════════════════════════ -->
    <div v-if="mapPicker.active" class="map-picker-overlay">
      <div class="map-picker-header">
        <span>📍 Clique sur la carte pour définir le centre</span>
        <span v-if="mapPicker.pickedLat" class="map-picker-coords">{{ mapPicker.pickedLat }}, {{ mapPicker.pickedLng }}</span>
        <div class="map-picker-actions">
          <button class="btn-success" @click="confirmMapPicker">✓ Confirmer</button>
          <button class="btn-cancel" @click="closeMapPicker">✕ Annuler</button>
        </div>
      </div>
      <div id="db-map-picker-div" class="map-picker-map"></div>
    </div>

  </div>
</template>

<script>
/* eslint-disable */
import { adminUI } from '../i18n/adminUI';
import { DEFAULT_PLANS } from '../plans.js';

const SUPER_ADMINS = ['ephraimichael@gmail.com', 'eifoany@gmail.com'];

export default {
  data() {
    return {
      adminLang: localStorage.getItem('adminLang') || 'fr',
      currentUser: null,
      authLoading: true,
      authError: false,
      userPlan: null,       // null = legacy user, string = subscription user
      userPlanConfig: null, // plan config from Firebase
      verifSent: false,
      showCreateForm: false,
      newGameName: '',
      newDisplayName: '',
      urlManuallyEdited: false,
      showCreateCoords: false,
      showCloneCoords: false,
      newGameCfg: {
        questionsOrder: 'linkedToPointNumber',
        languages: ['he'],
        mapLat: '',
        mapLng: '',
        lastPoint: '',
        visitOrder: '1, 2, 3, 4',
      },
      nameCheckStatus: '',
      activeInfo: null,
      checking: false,
      creating: false,
      myGames: [],
      gameDisplayNames: {},
      loadingGames: false,
      toast: { show: false, text: '', type: 'success' },
      deleteConfirm: { show: false, name: '', typed: '', deleting: false },
      cloneModal: { show: false, sourceName: '', displayName: '', newName: '', mapLat: '', mapLng: '', nameStatus: '', cloning: false, urlManuallyEdited: false },
      mapPicker: { active: false, target: 'create', pickedLat: null, pickedLng: null, _map: null, _marker: null },
    };
  },

  mounted() {
    const firebase = require('firebase/app').default;
    firebase.auth().onAuthStateChanged(async (user) => {
      this.currentUser = user;
      this.authLoading = false;
      if (user && user.emailVerified) {
        await this.loadUserPlan(user);
        await this.loadMyGames();
      }
    });
  },

  computed: {
    canCreate() {
      const c = this.newGameCfg;
      const base = this.newGameName.trim() && c.languages.length > 0 && c.mapLat && c.mapLng;
      if (this.userPlan) return base;
      return base && c.lastPoint && c.visitOrder.trim();
    },
    canClone() {
      const m = this.cloneModal;
      return m.newName.trim() && m.displayName.trim() && m.mapLat && m.mapLng;
    },
  },

  methods: {

    t(key) {
      const lang = adminUI[this.adminLang] || adminUI.fr;
      return lang[key] || adminUI.fr[key] || key;
    },

    setAdminLang(lang) {
      this.adminLang = lang;
      localStorage.setItem('adminLang', lang);
    },

    async openMapPicker(target = 'create') {
      this.mapPicker.target = target;
      const src = target === 'clone' ? this.cloneModal : this.newGameCfg;
      this.mapPicker.pickedLat = parseFloat(src.mapLat) || null;
      this.mapPicker.pickedLng = parseFloat(src.mapLng) || null;
      this.mapPicker.active = true;
      await this.$gmapApiPromiseLazy();
      await this.$nextTick();

      let lat = this.mapPicker.pickedLat;
      let lng = this.mapPicker.pickedLng;
      if (!lat || !lng) {
        try {
          const pos = await new Promise((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
          );
          lat = pos.coords.latitude;
          lng = pos.coords.longitude;
        } catch {
          lat = 32.657;
          lng = 35.417;
        }
      }
      const center = { lat, lng };
      const map = new window.google.maps.Map(document.getElementById('db-map-picker-div'), {
        center, zoom: 13,
      });
      let marker = this.mapPicker.pickedLat
        ? new window.google.maps.Marker({ position: center, map })
        : null;
      map.addListener('click', (e) => {
        const newLat = parseFloat(e.latLng.lat().toFixed(7));
        const newLng = parseFloat(e.latLng.lng().toFixed(7));
        this.mapPicker.pickedLat = newLat;
        this.mapPicker.pickedLng = newLng;
        if (marker) marker.setMap(null);
        marker = new window.google.maps.Marker({ position: { lat: newLat, lng: newLng }, map });
      });
      this.mapPicker._map = map;
      this.mapPicker._marker = marker;
    },

    confirmMapPicker() {
      if (this.mapPicker.pickedLat !== null) {
        const dest = this.mapPicker.target === 'clone' ? this.cloneModal : this.newGameCfg;
        dest.mapLat = String(this.mapPicker.pickedLat);
        dest.mapLng = String(this.mapPicker.pickedLng);
      }
      this.closeMapPicker();
    },

    closeMapPicker() {
      this.mapPicker.active = false;
      this.mapPicker._map = null;
      this.mapPicker._marker = null;
    },
    async signIn() {
      this.authLoading = true;
      this.authError = false;
      const firebase = require('firebase/app').default;
      const provider = new firebase.auth.GoogleAuthProvider();
      try {
        const result = await firebase.auth().signInWithPopup(provider);
        // If email not verified, send verification
        if (!result.user.emailVerified) {
          await result.user.sendEmailVerification();
        }
      } catch {
        this.authError = true;
        this.authLoading = false;
      }
    },

    async signOut() {
      const firebase = require('firebase/app').default;
      await firebase.auth().signOut();
      this.currentUser = null;
      this.myGames = [];
    },

    async resendVerification() {
      try {
        await this.currentUser.sendEmailVerification();
        this.verifSent = true;
        setTimeout(() => { this.verifSent = false; }, 4000);
      } catch { /* ignore */ }
    },

    async recheckVerification() {
      await this.currentUser.reload();
      const firebase = require('firebase/app').default;
      this.currentUser = firebase.auth().currentUser;
      if (this.currentUser.emailVerified) {
        await this.loadMyGames();
      }
    },

    async loadUserPlan(user) {
      // Super admins are always legacy — full access, no restrictions
      if (SUPER_ADMINS.includes(user.email)) return;

      const firebase = require('firebase/app').default;
      const db = firebase.database();

      const planSnap = await db.ref('users/' + user.uid + '/plan').once('value');
      const storedPlan = planSnap.val();

      if (storedPlan) {
        // Existing subscription user
        this.userPlan = storedPlan;
      } else {
        // Check if they have existing games (legacy user)
        const games = await this.$store.dispatch('fetchUserGames', user.uid);
        if (games.length === 0) {
          // Truly new user → assign free plan
          await db.ref('users/' + user.uid).set({ plan: 'free', email: user.email, createdAt: Date.now() });
          this.userPlan = 'free';
        }
        // else: legacy user with games → userPlan stays null → no restrictions
      }

      if (this.userPlan) {
        const planSnap = await db.ref('/plans/' + this.userPlan).once('value');
        this.userPlanConfig = planSnap.val() || DEFAULT_PLANS[this.userPlan] || DEFAULT_PLANS.free;
      }
    },

    async loadMyGames() {
      this.loadingGames = true;
      this.myGames = await this.$store.dispatch('fetchUserGames', this.currentUser.uid);
      this.loadingGames = false;
      // Load display names for each game asynchronously
      const firebase = require('firebase/app').default;
      for (const name of this.myGames) {
        firebase.database().ref(name + '/displayName').once('value').then(snap => {
          if (snap.val()) this.gameDisplayNames = { ...this.gameDisplayNames, [name]: snap.val() };
        });
      }
    },

    async checkName() {
      const name = this.newGameName.trim();
      if (!name) return;
      this.checking = true;
      const available = await this.$store.dispatch('checkGameNameAvailable', name);
      this.nameCheckStatus = available ? 'available' : 'taken';
      this.checking = false;
    },

    async createGame() {
      const name = this.newGameName.trim();
      if (!name) return;
      this.creating = true;
      const available = await this.$store.dispatch('checkGameNameAvailable', name);
      if (!available) {
        this.nameCheckStatus = 'taken';
        this.creating = false;
        return;
      }
      const c = this.newGameCfg;
      const langs = c.languages.length ? c.languages : ['he'];

      // For subscription users: auto-set points, lastPoint and visitOrder
      const isSubscription = !!this.userPlan && !!this.userPlanConfig;
      const maxPts = isSubscription ? (this.userPlanConfig.maxPoints || 3) : null;
      const visitArr = isSubscription
        ? Array.from({ length: maxPts }, (_, i) => i + 1)
        : c.visitOrder.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n));
      const lastPoint = isSubscription ? maxPts : (parseInt(c.lastPoint) || 1);

      // Points are created by the wizard in adminUser — start with empty structure
      const langData = {};
      langs.forEach(l => { langData[l] = { questions: [] }; });

      const gameData = {
        uid: this.currentUser.uid,
        navigationName: name,
        displayName: this.newDisplayName.trim() || name,
        questionsOrder: c.questionsOrder,
        randomQuestions: false,
        languages: langs,
        ...langData,
        pointsOrder: { order1: visitArr.length ? visitArr : [1] },
        markers: [],
        mapParameters: { lat: parseFloat(c.mapLat) || 32.657, lng: parseFloat(c.mapLng) || 35.417, zoom: 15 },
        lastPoint,
      };
      await this.$store.dispatch('createNewGame', gameData);
      this.myGames.push(name);
      this.showCreateForm = false;
      this.newGameName = '';
      this.newDisplayName = '';
      this.urlManuallyEdited = false;
      this.nameCheckStatus = '';
      this.activeInfo = null;
      this.newGameCfg = { questionsOrder: 'linkedToPointNumber', languages: ['he'], mapLat: '', mapLng: '', lastPoint: '', visitOrder: '1, 2, 3, 4' };
      this.showCreateCoords = false;
      this.creating = false;
      this.showToast('Parcours créé ! Tu peux maintenant l\'éditer.', 'success');
      // Go directly to editor
      this.$router.push('/adminUser?game=' + name);
    },

    toSlug(str) {
      return str
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // enlève les accents
        .replace(/\s+/g, '')                               // enlève les espaces
        .replace(/[^a-zA-Z0-9_-]/g, '');                  // enlève les caractères spéciaux
    },

    onDisplayNameInput() {
      if (!this.urlManuallyEdited) {
        this.newGameName = this.toSlug(this.newDisplayName);
        this.nameCheckStatus = '';
      }
    },

    toggleInfo(field) {
      this.activeInfo = this.activeInfo === field ? null : field;
    },

    askDelete(name) {
      this.deleteConfirm = { show: true, name, typed: '', deleting: false };
    },

    async confirmDelete() {
      const name = this.deleteConfirm.name;
      this.deleteConfirm.deleting = true;
      const firebase = require('firebase/app').default;
      const db = firebase.database();
      await Promise.all([
        db.ref(name).remove(),
        db.ref('gameNames/' + name).remove(),
        db.ref('userGames/' + this.currentUser.uid + '/' + name).remove(),
      ]);
      this.myGames = this.myGames.filter(g => g !== name);
      this.deleteConfirm = { show: false, name: '', typed: '', deleting: false };
      this.showToast('Parcours « ' + name + ' » supprimé.', 'success');
    },

    askClone(sourceName) {
      this.cloneModal = {
        show: true,
        sourceName,
        displayName: (this.gameDisplayNames[sourceName] || sourceName) + ' — copie',
        newName: this.toSlug((this.gameDisplayNames[sourceName] || sourceName) + 'Copie'),
        mapLat: '',
        mapLng: '',
        nameStatus: '',
        cloning: false,
        urlManuallyEdited: false,
      };
      // Pre-fill map coords from source
      const firebase = require('firebase/app').default;
      firebase.database().ref(sourceName + '/mapParameters').once('value').then(snap => {
        const mp = snap.val() || {};
        this.cloneModal.mapLat = String(mp.lat || '');
        this.cloneModal.mapLng = String(mp.lng || '');
      });
    },

    onCloneDisplayNameInput() {
      if (!this.cloneModal.urlManuallyEdited) {
        this.cloneModal.newName = this.toSlug(this.cloneModal.displayName);
        this.cloneModal.nameStatus = '';
      }
    },

    async cloneGame() {
      const newName = this.cloneModal.newName.trim();
      if (!newName) return;
      this.cloneModal.cloning = true;

      // Check availability
      const available = await this.$store.dispatch('checkGameNameAvailable', newName);
      if (!available) {
        this.cloneModal.nameStatus = 'taken';
        this.cloneModal.cloning = false;
        return;
      }

      const firebase = require('firebase/app').default;
      const db = firebase.database();

      // Read full source data
      const snap = await db.ref(this.cloneModal.sourceName).once('value');
      const sourceData = snap.val();
      if (!sourceData) {
        this.showToast('Erreur : impossible de lire le parcours source.', 'error');
        this.cloneModal.cloning = false;
        return;
      }

      // Build cloned data — override identity fields
      const clonedData = {
        ...sourceData,
        navigationName: newName,
        displayName: this.cloneModal.displayName.trim(),
        uid: this.currentUser.uid,
        mapParameters: {
          ...(sourceData.mapParameters || {}),
          lat: parseFloat(this.cloneModal.mapLat) || sourceData.mapParameters?.lat || 32.657,
          lng: parseFloat(this.cloneModal.mapLng) || sourceData.mapParameters?.lng || 35.417,
        },
      };

      // Write to Firebase
      await db.ref(newName).set(clonedData);
      await db.ref('gameNames/' + newName).set(true);
      await db.ref('userGames/' + this.currentUser.uid + '/' + newName).set(true);

      // Update local list
      this.myGames.push(newName);
      this.gameDisplayNames = { ...this.gameDisplayNames, [newName]: clonedData.displayName };
      this.cloneModal.show = false;
      this.showToast('Parcours cloné avec succès ! Tu peux maintenant l\'éditer.', 'success');
      this.$router.push('/adminUser?game=' + newName);
    },

    editGame(name) {
      this.$router.push('/adminUser?game=' + name);
    },

    gameUrl(name) {
      return window.location.origin + '/' + name;
    },

    copyUrl(name) {
      navigator.clipboard.writeText(this.gameUrl(name)).then(() => {
        this.showToast('URL copiée !', 'success');
      });
    },

    showToast(text, type = 'success') {
      this.toast = { show: true, text, type };
      setTimeout(() => { this.toast.show = false; }, 2500);
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }
.db-root { min-height: 100vh; background: #f0f4f8; font-family: system-ui, sans-serif; direction: ltr; }

/* Center screens (auth / verify) */
.center-screen {
  display: flex; align-items: center; justify-content: center;
  min-height: 100vh; background: linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%);
}
.db-card {
  background: white; border-radius: 16px; padding: 40px 32px;
  width: 100%; max-width: 400px; box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  text-align: center;
}
.db-logo { font-size: 52px; margin-bottom: 12px; }
.db-card h2 { color: #1a1a2e; margin: 0 0 8px; font-size: 24px; }
.sub { color: #666; font-size: 14px; margin-bottom: 20px; }

/* Topbar */
.db-topbar {
  background: #1a73e8; color: white; padding: 0 20px;
  display: flex; align-items: center; justify-content: space-between;
  min-height: 56px; flex-wrap: wrap; gap: 8px;
}
.db-topbar-left { display: flex; align-items: center; gap: 10px; }
.db-logo-sm { font-size: 24px; }
.db-title { font-weight: 700; font-size: 16px; }
.db-topbar-right { display: flex; align-items: center; gap: 12px; }
.db-email { font-size: 13px; opacity: 0.85; }

/* Content */
.db-content { max-width: 760px; margin: 0 auto; padding: 24px 16px; }
.db-section {
  background: white; border-radius: 14px; padding: 24px;
  margin-bottom: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.08);
}
.db-section-title { font-size: 17px; font-weight: 700; color: #333; margin-bottom: 16px; }

/* Create form */
.create-form { background: #f8faff; border-radius: 10px; padding: 16px; border: 1px solid #d0deff; }
.create-notice { background: #fffbea; border: 1px solid #f0d060; border-radius: 8px; padding: 10px 14px; font-size: 13px; color: #7a5c00; margin-bottom: 16px; }
.field-group { margin-bottom: 16px; }
.field-label-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
.field-label { font-size: 12px; font-weight: 700; color: #555; text-transform: uppercase; }
.info-icon { cursor: pointer; font-size: 14px; user-select: none; opacity: 0.7; transition: opacity 0.15s; }
.info-icon:hover { opacity: 1; }
.info-box { background: #e8f0fe; border-left: 3px solid #1a73e8; border-radius: 6px; padding: 10px 12px; font-size: 12px; color: #1a3a8f; line-height: 1.6; margin-bottom: 8px; }
.coords-row { display: flex; gap: 10px; align-items: center; }
.coords-row .form-input { flex: 1; }
.btn-map-centered { flex: 1; }
.btn-coords-toggle { padding: 7px 10px; background: #f1f5f9; color: #64748b; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 14px; cursor: pointer; white-space: nowrap; }
.btn-coords-toggle:hover { background: #e2e8f0; color: #334155; }
.btn-coords-toggle.active { background: #e0f2fe; color: #0369a1; border-color: #7dd3fc; }
.lang-checks { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 4px; }
.check-label { display: flex; align-items: center; gap: 5px; font-size: 13px; cursor: pointer; }
.hint { font-size: 12px; color: #888; margin: 0 0 6px; }
code { background: #eef2ff; color: #3949ab; border-radius: 3px; padding: 1px 5px; font-size: 12px; }
.form-input {
  width: 100%; padding: 10px 14px; border: 1.5px solid #ddd; border-radius: 8px;
  font-size: 14px; outline: none; transition: border 0.15s;
}
.form-input:focus { border-color: #1a73e8; }

/* Games grid */
.games-grid { display: flex; flex-direction: column; gap: 12px; }
.game-card {
  border: 1px solid #e0e7ff; border-radius: 10px; padding: 16px;
  background: #f8faff; display: flex; flex-direction: column; gap: 8px;
}
.game-card-name { font-size: 17px; font-weight: 700; color: #1a1a2e; }
.game-card-slug { font-size: 11px; color: #888; margin-top: -4px; margin-bottom: 4px; }
.game-card-url { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.url-label { font-size: 12px; color: #888; font-weight: 600; }
.url-link { font-size: 13px; color: #1a73e8; text-decoration: none; word-break: break-all; }
.url-link:hover { text-decoration: underline; }
.game-card-actions { display: flex; gap: 8px; margin-top: 4px; }
.empty-state { text-align: center; color: #999; padding: 30px; font-size: 15px; }

/* Buttons */
.btn-google {
  width: 100%; padding: 13px; background: white; color: #3c4043;
  border: 1px solid #dadce0; border-radius: 10px; font-size: 15px; font-weight: 500;
  cursor: pointer; margin-top: 8px; transition: box-shadow 0.15s;
  display: flex; align-items: center; justify-content: center;
}
.btn-google:hover { box-shadow: 0 1px 6px rgba(0,0,0,0.2); }
.btn-google:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-create {
  padding: 12px 24px; background: #1a73e8; color: white; border: none;
  border-radius: 10px; font-size: 15px; font-weight: 600; cursor: pointer;
  transition: background 0.15s;
}
.btn-create:hover { background: #1557b0; }
.plan-limit-msg {
  padding: 12px 16px; background: #fef9c3; border: 1px solid #fde047;
  border-radius: 10px; font-size: 13px; color: #713f12; display: flex;
  align-items: center; gap: 10px; flex-wrap: wrap;
}
.plan-upgrade-link {
  color: #1a73e8; font-weight: 700; text-decoration: none;
}
.plan-upgrade-link:hover { text-decoration: underline; }
.btn-success {
  padding: 9px 18px; background: #2e7d32; color: white;
  border: none; border-radius: 8px; font-size: 14px; font-weight: 600;
  cursor: pointer; transition: background 0.15s;
}
.btn-success:hover { background: #1b5e20; }
.btn-success:disabled { background: #9e9e9e; cursor: not-allowed; }
.btn-check {
  padding: 9px 18px; background: #1a73e8; color: white;
  border: none; border-radius: 8px; font-size: 14px; font-weight: 600;
  cursor: pointer; transition: background 0.15s;
}
.btn-check:hover { background: #1557b0; }
.btn-check:disabled { background: #9e9e9e; cursor: not-allowed; }
.btn-secondary {
  padding: 9px 18px; background: #e0e0e0; color: #333;
  border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;
}
.btn-secondary:hover { background: #bdbdbd; }
.btn-ghost {
  display: block; width: 100%; padding: 10px; background: transparent;
  color: #1a73e8; border: 1.5px dashed #90caf9; border-radius: 8px;
  font-size: 14px; font-weight: 600; cursor: pointer; margin-top: 8px;
}
.btn-ghost:hover { background: #e8f0fe; }
.btn-link {
  display: block; background: none; border: none; color: #999;
  font-size: 13px; cursor: pointer; margin-top: 8px; text-decoration: underline;
}
.btn-logout {
  padding: 6px 12px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.4);
  background: transparent; color: white; font-size: 12px; cursor: pointer;
}
.db-admin-lang { display: flex; gap: 4px; align-items: center; }
.db-lang-bar-top {
  display: flex; justify-content: flex-end; gap: 6px;
  padding: 8px 16px;
  background: white; border-bottom: 1px solid #f1f5f9;
  position: sticky; top: 0; z-index: 100;
}
.db-lang-btn {
  background: white; border: 1.5px solid #e2e8f0;
  border-radius: 20px; padding: 4px 10px;
  font-size: 15px; cursor: pointer; transition: all 0.15s;
  color: #64748b;
}
.db-lang-btn:hover { background: #f1f5f9; }
.db-lang-btn.db-lang-active { background: #1a73e8; border-color: #1a73e8; }
.btn-edit-game {
  padding: 7px 16px; background: #e3f2fd; color: #1565c0;
  border: 1px solid #90caf9; border-radius: 7px; font-size: 13px;
  font-weight: 600; cursor: pointer; transition: background 0.15s;
}
.btn-edit-game:hover { background: #bbdefb; }
.btn-clone-game {
  padding: 7px 16px; background: #f3e5f5; color: #6a1b9a;
  border: 1px solid #ce93d8; border-radius: 7px; font-size: 13px;
  font-weight: 600; cursor: pointer; transition: background 0.15s;
}
.btn-clone-game:hover { background: #e1bee7; }
.btn-delete-game {
  padding: 7px 16px; background: #fff0f0; color: #c62828;
  border: 1px solid #ffcdd2; border-radius: 7px; font-size: 13px;
  font-weight: 600; cursor: pointer; transition: background 0.15s;
}
.btn-delete-game:hover { background: #ffcdd2; }
/* Delete modal */
.modal-overlay {
  position: fixed; inset: 0; z-index: 2000;
  background: rgba(0,0,0,0.55); display: flex;
  align-items: center; justify-content: center; padding: 20px;
}
.modal-card {
  background: white; border-radius: 16px; padding: 28px 24px;
  max-width: 420px; width: 100%; box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}
.modal-icon { font-size: 40px; text-align: center; margin-bottom: 8px; }
.modal-title { font-size: 20px; font-weight: 800; color: #c62828; text-align: center; margin-bottom: 12px; }
.modal-body { font-size: 14px; color: #444; line-height: 1.6; text-align: center; margin-bottom: 16px; }
.modal-input-row { margin-bottom: 20px; }
.modal-input-label { font-size: 12px; font-weight: 700; color: #666; text-transform: uppercase; }
.modal-actions { display: flex; gap: 10px; justify-content: flex-end; }
.btn-danger {
  padding: 9px 20px; background: #c62828; color: white;
  border: none; border-radius: 8px; font-size: 14px; font-weight: 700;
  cursor: pointer; transition: background 0.15s;
}
.btn-danger:hover:not(:disabled) { background: #b71c1c; }
.btn-danger:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-copy {
  padding: 2px 7px; background: #f0f0f0; border: 1px solid #ddd;
  border-radius: 5px; cursor: pointer; font-size: 13px;
}
.btn-copy:hover { background: #e0e0e0; }

/* Messages */
.msg { padding: 10px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; }
.msg.error { background: #ffebee; color: #c62828; }
.msg.success { background: #e8f5e9; color: #2e7d32; }

/* Misc */
.row-buttons { display: flex; gap: 10px; justify-content: flex-end; flex-wrap: wrap; }
.mt-1 { margin-top: 6px; }
.mt-2 { margin-top: 8px; }
.mt-3 { margin-top: 12px; }

/* Spinner */
.spinner-wrap { display: flex; justify-content: center; padding: 30px; }
.spinner {
  width: 32px; height: 32px; border: 3px solid #e0e0e0;
  border-top-color: #1a73e8; border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Toast */
.toast-msg {
  position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
  padding: 12px 24px; border-radius: 10px; font-weight: 600;
  font-size: 14px; z-index: 9999; box-shadow: 0 4px 20px rgba(0,0,0,0.2);
}
.toast-msg.success { background: #2e7d32; color: white; }
.toast-msg.error { background: #c62828; color: white; }

/* Map picker */
.btn-map-pick {
  padding: 8px 12px; background: #e8f0fe; border: 1.5px solid #1a73e8;
  border-radius: 8px; font-size: 16px; cursor: pointer; flex-shrink: 0;
  transition: background 0.15s;
}
.btn-map-pick:hover { background: #c5d8fd; }
.map-picker-overlay {
  position: fixed; inset: 0; z-index: 10100;
  display: flex; flex-direction: column; background: #000;
}
.map-picker-header {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  background: #1a1a2e; color: white; padding: 10px 16px;
  font-size: 14px; flex-shrink: 0;
}
.map-picker-coords {
  background: rgba(255,255,255,0.15); padding: 3px 10px;
  border-radius: 12px; font-family: monospace; font-size: 13px;
}
.map-picker-actions { display: flex; gap: 8px; margin-left: auto; }
.map-picker-actions .btn-success {
  padding: 6px 16px; background: #2e7d32; color: white;
  border: none; border-radius: 6px; cursor: pointer; font-size: 13px;
}
.map-picker-actions .btn-cancel {
  padding: 6px 16px; background: #c62828; color: white;
  border: none; border-radius: 6px; cursor: pointer; font-size: 13px;
}
.map-picker-map { flex: 1; width: 100%; }
</style>
